import { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { Helper } from '@react-three/drei'
import { getAdapter } from '../lib/transformAdapters'
import { sphericalToXYZ } from '../lib/lightPlacement'

// A movable point light with a visible body — the probe for "is this actually
// being reflected?". Every other light in the scene is a DIRECTIONAL (shadow
// sun, live key, the edit-mode em_* pair), which carries an angle and nothing
// else: moving one cannot walk a highlight across a surface. This one is
// POSITIONAL, so its direction to the subject changes as it moves, which is what
// makes a specular response visible while you drag it.
//
// The light and the cube share ONE group, so the sliders, the viewport gizmo
// and the headlamp all drive a single transform — nothing to keep in sync.
//
// Dragging it in the viewport is the existing ENG-895 gizmo, not one of our
// own: `createLightAdapter` puts a row in the outliner for each point light,
// and the gizmo's camera stand-down, snapping and undo come with it. A second
// TransformControls here would be the exact duplication TransformGizmo.jsx's
// header warns about.
//
// The cube is deliberately UNLIT (MeshBasicMaterial, toneMapped off) for the
// same reason the backdrop ground is: ambient and sun intensities both default
// to 0, so a lit material would render the emitter black in exactly the empty
// scene it's meant to light. It stands in for a source; it is not a shaded prop.
//
// Since ENG-1190 SceneLights mounts one of these per `point` entry in
// liveTuneStore.lights; `id` is the entry's key and `torch` the entry. Every
// instance publishes under window.__LIGHTS__[id]; the one with the legacy id
// `torch` ALSO publishes __TORCH__ (CLAUDE.md forbids renaming it), because it
// mounts as a sibling of the avatar group and so is NOT reachable by traversing
// __AVATAR_SCENE__ (same reason as __SPOTLIGHT_TARGET__ / __CAMERA_CAGE_GIZMO__).

export default function TorchLight({ torch, id = 'torch', selected = false }) {
  const group = useRef(null)
  const camera = useThree((s) => s.camera)
  const home = useMemo(
    () => sphericalToXYZ(torch.distance, torch.elevation, torch.azimuth, [0, torch.targetHeight, 0]),
    [torch.distance, torch.elevation, torch.azimuth, torch.targetHeight],
  )

  // Headlamp. Per-frame writes stay on the THREE object — anything reading the
  // light reads the live scene, not the store, so a moving torch needs no store
  // traffic (and a per-frame setState would re-render the panel every frame). It
  // owns the position outright while on, which is why the adapter makes the
  // torch ungrabbable then rather than letting a drag be silently overwritten.
  useFrame(() => {
    if (!torch.followCamera) return
    if (group.current) camera.getWorldPosition(group.current.position)
  })

  // Re-assert the placement the moment the headlamp lets go. The `position`
  // prop can't do it alone: R3F skips a write when the VALUE is unchanged, so
  // releasing without also touching a knob would leave the group parked at the
  // last camera position while the sliders — the actual truth — say elsewhere.
  useEffect(() => {
    if (torch.followCamera) return
    group.current?.position.set(home[0], home[1], home[2])
  }, [home, torch.followCamera])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const pos = () => {
      const g = group.current
      return g ? [g.position.x, g.position.y, g.position.z] : null
    }
    const handle = {
      type: 'point',
      get enabled() { return !!torch.enabled },
      get followCamera() { return !!torch.followCamera },
      group,
      // Live world position. The headlamp and an in-flight drag move the group
      // without a store write, so the spherical knobs are NOT the truth then.
      pos,
      // Move it in world meters, through the SAME adapter the gizmo drags, so
      // there's one write path and one refusal rule. Returns the stored knobs,
      // or null when it didn't land (off, or the headlamp owns the position).
      setPos: (x, y, z) => getAdapter(id)?.write({ position: [x, y, z] }) ?? null,
      state: () => ({
        enabled: !!torch.enabled,
        followCamera: !!torch.followCamera,
        intensity: torch.intensity,
        color: torch.color,
        home,
        pos: pos(),
      }),
    }
    window.__LIGHTS__ = window.__LIGHTS__ ?? {}
    window.__LIGHTS__[id] = handle
    if (id === 'torch') window.__TORCH__ = handle
    return () => {
      if (window.__LIGHTS__?.[id] === handle) delete window.__LIGHTS__[id]
      if (id === 'torch' && window.__TORCH__ === handle) delete window.__TORCH__
    }
  }, [torch, home, id])

  if (!torch.enabled) return null

  return (
    <group ref={group} name={id} position={home}>
      <pointLight name={`${id}_light`} color={torch.color} intensity={torch.intensity} distance={torch.range} decay={2}>
        {/* Selected in the outliner: draw the light's own marker, as Unreal does
            for a selected point light. Its range sphere follows below when the
            range is finite. */}
        {selected && <Helper type={THREE.PointLightHelper} args={[0.12]} />}
      </pointLight>
      {selected && torch.range > 0 && (
        <mesh name={`${id}_range`}>
          <sphereGeometry args={[torch.range, 24, 16]} />
          <meshBasicMaterial color={torch.color} wireframe transparent opacity={0.25} toneMapped={false} depthWrite={false} />
        </mesh>
      )}
      {/* Hidden in headlamp mode: sitting on the camera, it's inside the near plane. */}
      {torch.cube && !torch.followCamera && (
        <mesh name={`${id}_cube`}>
          <boxGeometry args={[torch.cubeSize, torch.cubeSize, torch.cubeSize]} />
          <meshBasicMaterial color={torch.color} toneMapped={false} />
        </mesh>
      )}
    </group>
  )
}
